import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2 } from 'lucide-react';
import { useOrg } from '../../context/OrgContext';
import { showToast } from '../../utils/alerts-messages';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/svg+xml', 'image/webp'];
const MAX_SIZE = 2 * 1024 * 1024; // 2 MB

export function AccountProfileSection() {
    const { org, loading, saveOrg, uploadLogo, deleteLogo } = useOrg();
    const [form, setForm] = useState({ name: '', formal_name: '', billing_email: '', contact_url: '', authored_language: 'en' });
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        if (org) {
            setForm({
                name: org.name || '',
                formal_name: org.formal_name || '',
                billing_email: org.billing_email || '',
                contact_url: org.contact_url || '',
                authored_language: org.authored_language || 'en',
            });
            setDirty(false);
        }
    }, [org]);

    const handleChange = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setDirty(true);
        setSaved(false);
    };

    const handleSave = async () => {
        const updates = {};
        if (form.name !== (org?.name || '')) updates.name = form.name;
        if (form.formal_name !== (org?.formal_name || '')) updates.formal_name = form.formal_name;
        if (form.billing_email !== (org?.billing_email || '')) updates.billing_email = form.billing_email;
        if (form.contact_url !== (org?.contact_url || '')) updates.contact_url = form.contact_url;
        if (form.authored_language !== (org?.authored_language || 'en')) updates.authored_language = form.authored_language;

        if (Object.keys(updates).length === 0) return;

        try {
            setSaving(true);
            await saveOrg(updates);
            setDirty(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch {
            // Error is handled by OrgContext
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <div style={styles.header}>
                <h2 style={styles.title}>Organization Profile</h2>
                <p style={styles.subtitle}>Manage your organization's identity and contact information.</p>
                {! (form.name && form.formal_name && form.billing_email && form.contact_url && org?.logo_url) && (
                    <p style={styles.bonaFideNotice}>
                        The Organizational Profile must be completed with bona-fide contact and representational
                        information. Deceptive or erroneous data is a violation of the Terms of Service.
                    </p>
                )}
            </div>

            <div style={styles.card}>
                <div style={styles.fieldGroup}>
                    <label style={styles.label}>Display Name</label>
                    <input
                        type="text"
                        value={form.name}
                        onChange={e => handleChange('name', e.target.value)}
                        placeholder="My Organization"
                        style={styles.input}
                    />
                    <span style={styles.hint}>Appears in the console header and member invitations.</span>
                </div>

                <div style={styles.fieldGroup}>
                    <label style={styles.label}>Legal / Formal Name</label>
                    <input
                        type="text"
                        value={form.formal_name}
                        onChange={e => handleChange('formal_name', e.target.value)}
                        placeholder="Acme Corporation LLC"
                        style={styles.input}
                    />
                    <span style={styles.hint}>Official name for billing and legal correspondence.</span>
                </div>

                <div style={styles.fieldGroup}>
                    <label style={styles.label}>Billing Email</label>
                    <input
                        type="email"
                        value={form.billing_email}
                        onChange={e => handleChange('billing_email', e.target.value)}
                        placeholder="billing@yourorg.com"
                        style={styles.input}
                    />
                    <span style={styles.hint}>Where invoices and billing notifications are sent.</span>
                </div>

                <div style={styles.fieldGroup}>
                    <label style={styles.label}>Organization Logo</label>
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".jpg,.jpeg,.png,.svg,.webp"
                        style={{ display: 'none' }}
                        onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            if (!ACCEPTED_TYPES.includes(file.type)) {
                                showToast('Accepted formats: JPG, PNG, SVG, WebP', 'error');
                                return;
                            }
                            if (file.size > MAX_SIZE) {
                                showToast('File must be under 2 MB', 'error');
                                return;
                            }
                            try {
                                setUploading(true);
                                await uploadLogo(file);
                            } catch { /* error set by context */ }
                            finally { setUploading(false); }
                            e.target.value = '';
                        }}
                    />

                    {org?.logo_url ? (
                        <div style={styles.logoRow}>
                            <div style={styles.logoContainer}>
                                <img
                                    src={org.logo_url}
                                    alt="Organization logo"
                                    style={styles.logoPreview}
                                    onError={e => { e.target.style.display = 'none'; }}
                                    onLoad={e => {
                                        const { naturalWidth, naturalHeight } = e.target;
                                        e.target.title = `${naturalWidth}×${naturalHeight}px`;
                                    }}
                                />
                            </div>
                            <div style={styles.logoActions}>
                                <button
                                    onClick={() => fileRef.current?.click()}
                                    disabled={uploading}
                                    style={styles.logoBtn}
                                >
                                    <Upload size={14} style={{ marginRight: 6 }} />
                                    {uploading ? 'Uploading…' : 'Replace'}
                                </button>
                                <button
                                    onClick={async () => {
                                        try { await deleteLogo(); } catch { /* context */ }
                                    }}
                                    disabled={uploading}
                                    style={{ ...styles.logoBtn, color: '#F87171' }}
                                >
                                    <Trash2 size={14} style={{ marginRight: 6 }} />
                                    Remove
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div
                            style={{
                                ...styles.dropZone,
                                ...(dragOver ? styles.dropZoneActive : {}),
                            }}
                            onClick={() => fileRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={async (e) => {
                                e.preventDefault();
                                setDragOver(false);
                                const file = e.dataTransfer.files?.[0];
                                if (!file) return;
                                if (!ACCEPTED_TYPES.includes(file.type)) {
                                    showToast('Accepted formats: JPG, PNG, SVG, WebP', 'error');
                                    return;
                                }
                                if (file.size > MAX_SIZE) {
                                    showToast('File must be under 2 MB', 'error');
                                    return;
                                }
                                try {
                                    setUploading(true);
                                    await uploadLogo(file);
                                } catch { /* context */ }
                                finally { setUploading(false); }
                            }}
                        >
                            <Upload size={24} style={{ color: '#64748B', marginBottom: 8 }} />
                            <span style={{ color: '#94A3B8', fontSize: 13 }}>
                                {uploading ? 'Uploading…' : 'Drop logo here or click to choose'}
                            </span>
                            <span style={{ color: '#B4C0D3', fontSize: 12, marginTop: 4 }}>
                                JPG, PNG, SVG, WebP — max 2 MB
                            </span>
                        </div>
                    )}
                </div>

                <div style={styles.fieldGroup}>
                    <label style={styles.label}>Organization Contact URL</label>
                    <input
                        type="url"
                        value={form.contact_url}
                        onChange={e => handleChange('contact_url', e.target.value)}
                        placeholder="https://yourorg.com/contact"
                        style={styles.input}
                    />
                    <span style={styles.hint}>Appears as the 'contact us' link on your surveys. Recipients use this to reach your organization with questions and to review your privacy policy. You must make a privacy policy available to any recipients per account subscription agreement.</span>
                </div>

                <div style={styles.fieldGroup}>
                    <label style={styles.label}>Authored Language</label>
                    <select
                        value={form.authored_language}
                        onChange={e => handleChange('authored_language', e.target.value)}
                        style={{ ...styles.input, maxWidth: 280 }}
                    >
                        <option value="en">English</option>
                        <option value="es">Spanish</option>
                        <option value="de">German</option>
                        <option value="fr">French</option>
                        <option value="it">Italian</option>
                        <option value="pt">Portuguese</option>
                        <option value="nl">Dutch</option>
                        <option value="ja">Japanese</option>
                        <option value="ko">Korean</option>
                        <option value="zh">Chinese</option>
                        <option value="ru">Russian</option>
                        <option value="ar">Arabic</option>
                        <option value="hi">Hindi</option>
                        <option value="pl">Polish</option>
                        <option value="sv">Swedish</option>
                        <option value="da">Danish</option>
                        <option value="fi">Finnish</option>
                        <option value="nb">Norwegian</option>
                        <option value="tr">Turkish</option>
                    </select>
                    <span style={styles.hint}>The language your team uses to author survey content. Non-input nodes will be translated when the survey targets a different language.</span>
                </div>

                <div style={styles.actionRow}>
                    <button
                        onClick={handleSave}
                        disabled={!dirty || saving || loading}
                        style={{
                            ...styles.saveBtn,
                            opacity: !dirty || saving ? 0.5 : 1,
                            cursor: !dirty || saving ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {saving ? 'Saving…' : 'Save Changes'}
                    </button>
                    {saved && <span style={styles.savedLabel}>✓ Saved</span>}
                </div>

                {/* Read-only info */}
                <div style={styles.readonlySection}>
                    <div style={styles.readonlyRow}>
                        <span style={styles.readonlyLabel}>Organization ID</span>
                        <code style={styles.readonlyValue}>{org?.org_id || '—'}</code>
                    </div>
                    <div style={styles.readonlyRow}>
                        <span style={styles.readonlyLabel}>Slug</span>
                        <code style={styles.readonlyValue}>{org?.slug || '—'}</code>
                    </div>
                    <div style={styles.readonlyRow}>
                        <span style={styles.readonlyLabel}>Created</span>
                        <span style={styles.readonlyValue}>
                            {org?.created_at ? new Date(org.created_at).toLocaleDateString() : '—'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

const styles = {
    header: { marginBottom: 28 },
    title: {
        margin: 0,
        fontSize: 22,
        fontWeight: 700,
        color: '#F1F5F9',
        letterSpacing: '-0.02em',
    },
    subtitle: {
        margin: '6px 0 0',
        fontSize: 14,
        color: '#B4C0D3',
    },
    bonaFideNotice: {
        margin: '8px 0 0',
        fontSize: 14,
        color: '#FFFFFF',
        fontStyle: 'italic',
    },
    card: {
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 12,
        padding: '28px 32px',
    },
    fieldGroup: {
        marginBottom: 22,
    },
    label: {
        display: 'block',
        fontSize: 15,
        fontWeight: 600,
        color: '#E2E8F0',
        marginBottom: 6,
    },
    input: {
        width: '100%',
        maxWidth: 480,
        padding: '10px 14px',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 8,
        color: '#F1F5F9',
        fontSize: 14,
        outline: 'none',
        transition: 'border-color 0.15s ease',
        boxSizing: 'border-box',
    },
    hint: {
        display: 'block',
        marginTop: 4,
        fontSize: 13,
        lineHeight: 1.5,
        color: '#B4C0D3',
    },
    logoRow: {
        display: 'flex', alignItems: 'center', gap: 20, marginTop: 8,
    },
    logoContainer: {
        width: 80, height: 80, borderRadius: 10,
        border: '1px solid rgba(255,255,255,0.1)',
        background: 'repeating-conic-gradient(rgba(255,255,255,0.06) 0% 25%, transparent 0% 50%) 50% / 16px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
    },
    logoPreview: {
        maxWidth: 72, maxHeight: 72, objectFit: 'contain',
    },
    logoActions: {
        display: 'flex', flexDirection: 'column', gap: 8,
    },
    logoBtn: {
        display: 'flex', alignItems: 'center', background: 'transparent',
        border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6,
        padding: '6px 14px', color: '#CBD5E1', fontSize: 13,
        cursor: 'pointer', transition: 'all 0.15s',
    },
    dropZone: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '32px 24px', marginTop: 8,
        border: '2px dashed rgba(255,255,255,0.1)', borderRadius: 10,
        cursor: 'pointer', transition: 'all 0.15s',
    },
    dropZoneActive: {
        borderColor: '#818CF8', background: 'rgba(99, 102, 241, 0.06)',
    },
    actionRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        marginTop: 8,
        marginBottom: 28,
        paddingBottom: 24,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
    },
    saveBtn: {
        padding: '10px 24px',
        background: '#3B82F6',
        border: 'none',
        borderRadius: 8,
        color: '#fff',
        fontSize: 14,
        fontWeight: 600,
        transition: 'all 0.15s ease',
    },
    savedLabel: {
        fontSize: 13,
        color: '#22C55E',
        fontWeight: 500,
    },
    readonlySection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
    },
    readonlyRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    readonlyLabel: {
        fontSize: 14,
        color: '#E2E8F0',
        fontWeight: 600,
        minWidth: 120,
    },
    readonlyValue: {
        fontSize: 13,
        color: '#B4C0D3',
        fontFamily: 'monospace',
    },
};
